import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { isCxsCompatibleTelSys } from '../../../NonReact/utils/CxsSubscriptionAccessUtils'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { CapabilityBitFlags } from '../../CMEAddModify/Constants'
import { isBitmaskMatching } from './BitwiseOperations'

const CX_SCORE_CAPABILITY_BY_TELSYS: Readonly<Partial<Record<TelSysTypes, CapabilityBitFlags>>> = {
    [TelSysTypes.BROADSOFT_BROADWORKS]: CapabilityBitFlags.CXS_BROADWORKS,
    [TelSysTypes.WEBEX_CALLING]: CapabilityBitFlags.CXS_WEBEX,
    [TelSysTypes.MS_TEAMS]: CapabilityBitFlags.CXS_TEAMS
}

const CX_TRANSCRIBE_CAPABILITY_BY_TELSYS: Readonly<Partial<Record<TelSysTypes, CapabilityBitFlags>>> = {
    [TelSysTypes.BROADSOFT_BROADWORKS]: CapabilityBitFlags.CXT_BROADWORKS,
    [TelSysTypes.WEBEX_CALLING]: CapabilityBitFlags.CXT_WEBEX,
    [TelSysTypes.MS_TEAMS]: CapabilityBitFlags.CXT_TEAMS
}

const CX_RECORD_CAPABILITY_BY_TELSYS: Readonly<Partial<Record<TelSysTypes, CapabilityBitFlags>>> = {
    [TelSysTypes.BROADSOFT_BROADWORKS]: CapabilityBitFlags.CXR_BROADWORKS,
    [TelSysTypes.WEBEX_CALLING]: CapabilityBitFlags.CXR_WEBEX,
    [TelSysTypes.MS_TEAMS]: CapabilityBitFlags.CXR_TEAMS
}

/**
 * Whether a CME allows a per-TelSys-type capability for the given TelSys.
 *
 * A Simulator emulating BroadWorks counts as BroadWorks, matching the backend. When the CME capabilities
 * or the TelSys type are not known yet (still loading, or not selected), the capability is treated as
 * allowed so the UI does not flicker it away; the backend still enforces it on save.
 *
 * @param cmeCapabilities The CME's {@code Capabilities} bitmask, or null/undefined when unknown.
 * @param capabilityByTelSys The capability bit for each TelSys type that has one.
 * @param telSysType The TelSys type, as a numeric id or name.
 * @param callControlOptions The TelSys {@code CallControlOptions}; only used to detect a Simulator emulating BroadWorks.
 * @returns False only when the CME is known not to have the capability for the TelSys type.
 */
const hasCmeCapabilityForTelSys = (
    cmeCapabilities: Nullable<number>,
    capabilityByTelSys: Readonly<Partial<Record<TelSysTypes, CapabilityBitFlags>>>,
    telSysType: Nullable<number | string>,
    callControlOptions?: Nullable<string>
): boolean => {
    if (!isVarNotEmpty(cmeCapabilities) || !isVarNotEmpty(telSysType) || telSysType === TelSysTypes.UNKNOWN) {
        return true
    }

    const effectiveTelSysType: number | string = isCxsCompatibleTelSys(telSysType, callControlOptions)
        ? TelSysTypes.BROADSOFT_BROADWORKS
        : telSysType
    const capability: Definable<CapabilityBitFlags> = capabilityByTelSys[effectiveTelSysType as TelSysTypes]

    // TelSys types without a capability bit (e.g. legacy telephony) never offer the product.
    if (!isVarNotEmpty(capability)) return false

    return isBitmaskMatching(cmeCapabilities, capability)
}

/**
 * Whether the CME allows CX Score for the TelSys type (APD-13052).
 *
 * @param cmeCapabilities The CME's {@code Capabilities} bitmask, or null/undefined when unknown.
 * @param telSysType The TelSys type, as a numeric id or name.
 * @param callControlOptions The TelSys {@code CallControlOptions}; only used to detect a Simulator emulating BroadWorks.
 * @returns False only when the CME is known not to have CX Score for the TelSys type.
 */
export const hasCmeCxScoreCapability = (
    cmeCapabilities: Nullable<number>,
    telSysType: Nullable<number | string>,
    callControlOptions?: Nullable<string>
): boolean => hasCmeCapabilityForTelSys(cmeCapabilities, CX_SCORE_CAPABILITY_BY_TELSYS, telSysType, callControlOptions)

/**
 * Whether the CME allows CX Record for the TelSys type (APD-13052).
 *
 * @param cmeCapabilities The CME's {@code Capabilities} bitmask, or null/undefined when unknown.
 * @param telSysType The TelSys type, as a numeric id or name.
 * @param callControlOptions The TelSys {@code CallControlOptions}; only used to detect a Simulator emulating BroadWorks.
 * @returns False only when the CME is known not to have CX Record for the TelSys type.
 */
export const hasCmeCxRecordCapability = (
    cmeCapabilities: Nullable<number>,
    telSysType: Nullable<number | string>,
    callControlOptions?: Nullable<string>
): boolean => hasCmeCapabilityForTelSys(cmeCapabilities, CX_RECORD_CAPABILITY_BY_TELSYS, telSysType, callControlOptions)

/**
 * Whether the CME allows AI Assist (one capability for every TelSys type, APD-13052).
 *
 * @param cmeCapabilities The CME's {@code Capabilities} bitmask, or null/undefined when unknown.
 * @returns False only when the CME is known not to have AI Assist.
 */
export const hasCmeAiAssistCapability = (cmeCapabilities: Nullable<number>): boolean =>
    !isVarNotEmpty(cmeCapabilities) || isBitmaskMatching(cmeCapabilities, CapabilityBitFlags.AI_ASSIST)

/**
 * Whether the CME allows CX Transcribe for the TelSys type (APD-14047).
 *
 * @param cmeCapabilities The CME's {@code Capabilities} bitmask, or null/undefined when unknown.
 * @param telSysType The TelSys type, as a numeric id or name.
 * @param callControlOptions The TelSys {@code CallControlOptions}; only used to detect a Simulator emulating BroadWorks.
 * @returns False only when the CME is known not to have CX Transcribe for the TelSys type.
 */
export const hasCmeCxTranscribeCapability = (
    cmeCapabilities: Nullable<number>,
    telSysType: Nullable<number | string>,
    callControlOptions?: Nullable<string>
): boolean =>
    hasCmeCapabilityForTelSys(cmeCapabilities, CX_TRANSCRIBE_CAPABILITY_BY_TELSYS, telSysType, callControlOptions)
